import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { TeComponent } from '../common/te-component';
import { TeTableRow } from './te-table-row';
import { TeTable } from './te-table';
import { styleMap } from 'lit-html/directives/style-map';
import '../te-spacing';
import { TeTableColumn } from './te-table-column';
import { theme } from '../common/theme';

/**
 * Table Cell Component
 *
 * @slot - Default slot for rendering cell content
 */
@customElement('te-table-cell')
export class TeTableCell extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-table-cell {
        display: flex;
        align-items: center;
        padding: 0 16px;
        flex: 1;
        position: relative;
      }

      .te-cell-content {
        display: flex;
        white-space: nowrap;
      }

      .te-table-cell.hover {
        background-color: ${theme.colors.shade1};
      }

      .te-table-cell.selected {
        background-color: ${theme.colors.primary.v0}0D;
      }
    `,
  ];

  /** Content alignment inside of cell */
  @property({ type: String }) align: 'flex-start' | 'center' | 'flex-end' = 'flex-start';

  @state() rowIndex = 0;
  @state() hover = false;
  @state() selected = false;
  @state() placeholderCellParent?: TeTable;
  @state() private columnIndex = 0;
  @state() private minWidth = 48;
  @state() private minHeight = 48;
  @state() private hasBeenUpdatedDynamically = false;

  @query('.te-cell-content') tableCell?: HTMLDivElement;

  disconnectedCallback() {
    this.placeholderCellParent = undefined;
  }

  getParentTable(): TeTable | null {
    if (this.placeholderCellParent) return this.placeholderCellParent;
    const p1 = this.parentElement;
    const p2 = p1?.parentElement;
    const table = p1?.nodeName === 'TE-TABLE' ? p1 : p2?.nodeName === 'TE-TABLE' ? p2 : null;
    return table ? (table as TeTable) : null;
  }

  getParentRow() {
    if (this.parentElement?.nodeName === 'TE-TABLE-ROW') return this.parentElement as TeTableRow;
    return null;
  }

  getParentColumn() {
    if (this.parentElement?.nodeName === 'TE-TABLE-COLUMN')
      return this.parentElement as TeTableColumn;
    return null;
  }

  getColumnIndex() {
    if (this.getParentColumn()) return 0;

    const parentRow = this.getParentRow();
    const parentTable = this.getParentTable();

    if (!parentTable || !parentRow) return 0;

    const baseCount = parentTable.hasStaticColumn ? 1 : 0;

    const nodes = [...parentRow.childNodes].filter((node) => node.nodeType === 1);

    return nodes.findIndex((node) => node === this) + baseCount;
  }

  getRowIndex() {
    const parentRow = this.getParentRow();

    if (parentRow) return parentRow.rowIndex;

    const parentTable = this.getParentTable();
    const parentColumn = this.getParentColumn();

    if (parentColumn && parentTable) {
      const baseCount = parentTable.hasStaticHeader || parentTable.hasStaticCorner ? 1 : 0;
      return (
        [...parentColumn.childNodes]
          .filter((node) => node.nodeName === 'TE-TABLE-CELL')
          .findIndex((node) => node === this) + baseCount
      );
    }

    return 0;
  }

  firstUpdated() {
    this.columnIndex = this.getColumnIndex();
    this.rowIndex = this.getRowIndex();
    const parentTable = this.getParentTable();

    if (!parentTable) return;

    // Initialize cells as minimum size
    this.setColumn(parentTable.minimumCellSize);
    this.setRow(parentTable.minimumCellSize);
  }

  handleSlotChange() {
    if (!this.hasBeenUpdatedDynamically) return;
    const parentTable = this.getParentTable();
    // Call for recalculation if slotted content changes after dynamic calculations
    parentTable?.resizeAllCells();
  }

  setColumn(value: number) {
    const parentTable = this.getParentTable();
    parentTable && parentTable.setColumn(this.columnIndex, value);
  }

  setRow(value: number) {
    const parentTable = this.getParentTable();
    parentTable && parentTable.setRow(this.rowIndex, value);
  }

  updateSize() {
    if (!this.tableCell) return;
    const rect = this.tableCell.getBoundingClientRect();
    this.setColumn(rect.width + 32);
    this.setRow(rect.height + 16);
    this.hasBeenUpdatedDynamically = true;
  }

  resizeCell() {
    const parentTable = this.getParentTable();
    if (!parentTable) return;
    this.minWidth = parentTable.getColumn(this.columnIndex);
    this.minHeight = parentTable.getRow(this.rowIndex);
  }

  getCellStyle() {
    const isMinimumSize = this.minWidth === 48 && this.minHeight === 48;

    let minWidth = this.minWidth;
    let minHeight = this.minHeight;

    if (isMinimumSize) {
      // For some reason, sometimes when minimized, the dimensions of the cell
      // revert to 48x48 internally, so fetch them from parent
      const parentTable = this.getParentTable();
      minWidth = parentTable?.getColumn(this.columnIndex) || minWidth;
      minHeight = parentTable?.getRow(this.rowIndex) || minHeight;
    }

    return {
      justifyContent: this.align,
      minWidth: minWidth + 'px',
      minHeight: minHeight + 'px',
    };
  }

  onMouseEnter(e: Event) {
    this.handleMouseEnter(e);
    const parentTable = this.getParentTable();
    if (parentTable) {
      parentTable.setRowHover(this.rowIndex, true);
    }
  }

  onMouseExit(e: Event) {
    this.handleMouseExit(e);
    const parentTable = this.getParentTable();
    if (parentTable) {
      parentTable.setRowHover(this.rowIndex, false);
    }
  }

  handleCellClick(e: Event) {
    if ((e.target as Node).nodeName === 'TE-CHECKBOX') return;
    this.handleClick(e);
  }

  render() {
    const classes = ['te-table-cell'];
    if (this.hover) classes.push('hover');
    if (this.selected) classes.push('selected');
    return html`
      <div
        class="${classes.join(' ')}"
        style=${styleMap(this.getCellStyle())}
        @click=${this.handleCellClick}
        @mouseenter=${this.onMouseEnter}
        @mouseleave=${this.onMouseExit}
      >
        <div class="te-cell-content">
          <slot @slotchange=${this.handleSlotChange}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-table-cell': TeTableCell;
  }
}
